/* eslint-disable @typescript-eslint/no-explicit-any */
import { motion } from "framer-motion";
import he from "he";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMusic } from "react-icons/fi";
import { IoMdClose } from "react-icons/io";
import { useParams } from "react-router";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import { KirtalLyricsPadListData } from "~/interface/kirtan-lyrics/kirtan-lyrics";
import { getSession } from "~/lib/helperFuncation/sessionStorageService";
import { getYoutubeVideoThumbnail } from "~/lib/helperFuncation/youtubeThumbnails";
import { touchVariants } from "~/lib/motion-obj";
import { useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { MstPersonType } from "~/type";
import { COLORS, LANG, SELECTED_PAD } from "~/utils/constant";
import { ApiService } from "~/utils/service";
import Chip from "../shared-components/Chip";
import Tag from "../shared-components/Tag";

// 🎨 CSS Shimmer animation - more performant than animate-pulse on mobile
const shimmerStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .skeleton-shimmer {
    background: linear-gradient(90deg, 
      rgba(0,0,0,0.05) 0%, 
      rgba(0,0,0,0.1) 20%, 
      rgba(0,0,0,0.05) 40%, 
      rgba(0,0,0,0.05) 100%);
    background-size: 800px 100%;
    animation: shimmer 2s infinite;
    will-change: background-position;
  }
`;

// Inject shimmer styles once
if (typeof document !== "undefined") {
  const styleId = "kirtan-skeleton-shimmer";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = shimmerStyles;
    document.head.appendChild(style);
  }
}

interface KirtanLyricsPadProps {
  kirtanPadListData: KirtalLyricsPadListData[];
  kirtanId: number | null;
  onClose: () => void;
  gayakData: MstPersonType[];
}

// ⚡ Cache for pad list data to avoid duplicate API calls
// Key format: "kirtanId-lang" to support multiple languages
const padDataCache = new Map<string, KirtalLyricsPadListData[]>();

// Helper function to generate cache key
const getCacheKey = (kirtanId: number, lang: string): string => `${kirtanId}-${lang}`;

const KirtanDetailPanel = ({ kirtanId, kirtanPadListData, onClose, gayakData }: KirtanLyricsPadProps) => {
  const { t } = useTranslation();
  const lang = useParams().lang;
  const { LocalizedLink } = useLocale();
  const padName = lang === LANG.gujarati ? "પદ" : "Pad";

  const { medium, mediumInter, normalInter } = useFontStyle();
  const { bhav, raagDhaal, rachayta, k_book, k_place, k_prasang } = useAppSelector((state) => state.kirtanLyrics);

  const [selectedPadList, setSelectedpad] = useState<KirtalLyricsPadListData>();
  const [padArray, setPadArray] = useState<string[]>([]);
  const [pad, setPad] = useState<string | null>(null);
  const [isLoadingPads, setIsLoadingPads] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ⚡ Memoize metadata lookups to prevent recalculation
  const KirtanInfo = useMemo(() => {
    // Handle array values for bhav_id and raag_dhal_id
    const BhavNames = selectedPadList?.bhav_id ? selectedPadList.bhav_id.map((id) => bhav?.find((item) => item.id === id)?.text).filter(Boolean) : [];
    const BhavName = BhavNames.length > 0 ? BhavNames : null;

    const RaagDhaalNames = selectedPadList?.raag_dhal_id
      ? selectedPadList.raag_dhal_id.map((id) => raagDhaal?.find((item) => item.id === id)?.text).filter(Boolean)
      : [];
    const RaagDhaalName = RaagDhaalNames.length > 0 ? RaagDhaalNames : null;

    const RachaytaName = rachayta?.find((item) => item.id === selectedPadList?.rachayta_id)?.text || null;
    const KBookName = k_book?.find((item) => item.id === selectedPadList?.book_id)?.text || null;
    const KPlaceName = k_place?.find((item) => item.id === selectedPadList?.place_id)?.text || null;
    const KPrasangName = k_prasang?.find((item) => item.id === selectedPadList?.prasang_id)?.text || null;

    return {
      [t("kirtan-lyrics.bhav", { defaultValue: "Bhav" })]: BhavName,
      [t("kirtan-lyrics.raag_dhal", { defaultValue: "Raag Dhaal" })]: RaagDhaalName,
      [t("kirtan-lyrics.rachayta", { defaultValue: "Rachayta" })]: RachaytaName,
      [t("kirtan-lyrics.k_book", { defaultValue: "K Book" })]: KBookName,
      [t("kirtan-lyrics.k_place", { defaultValue: "K Place" })]: KPlaceName,
      [t("kirtan-lyrics.k_prasang", { defaultValue: "K Prasang" })]: KPrasangName,
    };
  }, [selectedPadList, bhav, raagDhaal, rachayta, k_book, k_place, k_prasang, t]);

  const handleClickOnPad = (pad: string) => {
    const padNo = Number(pad.split("-")[1].trim());
    setPad(pad);
    sessionStorage.setItem(SELECTED_PAD, padNo.toString());

    // Use padDataCache as fallback if kirtanPadListData is not available
    const cacheKey = getCacheKey(kirtanId!, lang || LANG.gujarati);
    const _data = kirtanPadListData && kirtanPadListData.length > 0 ? kirtanPadListData : padDataCache.get(cacheKey) || [];

    const selectedPadData = _data?.find((kirtan) => kirtan.pad_no === padNo);
    setSelectedpad(selectedPadData);
  };

  // ⚡ Load pad data from cache or API
  useEffect(() => {
    (async () => {
      if (!kirtanId) return;

      const cacheKey = getCacheKey(kirtanId, lang || LANG.gujarati);
      // Priority 1: Check if loader data is available
      if (kirtanPadListData && kirtanPadListData.length > 0) {
        padDataCache.set(cacheKey, kirtanPadListData);
        setPadArray(kirtanPadListData.map((_, index) => `${padName} - ${index + 1}`));
        const sessionPad = getSession(SELECTED_PAD);
        // First try exact pad id match, then session pad, then first pad.
        // Using || inside a single find() is wrong — if sessionPad="1" appears
        // earlier in the array it wins over the kirtan.id match further down.
        const selectedPadData =
          kirtanPadListData.find((kirtan) => kirtan.id === kirtanId) ??
          (sessionPad ? kirtanPadListData.find((kirtan) => kirtan.pad_no === Number(sessionPad)) : undefined) ??
          kirtanPadListData[0];

        setSelectedpad(selectedPadData);
        const padValue = `${padName} - ${selectedPadData?.pad_no || 1}`;
        setPad(padValue);
        return;
      }

      // Priority 2: Check cache (fallback for hard reload)
      if (padDataCache.has(cacheKey) && padDataCache.get(cacheKey)!.length > 0) {
        const cachedData = padDataCache.get(cacheKey)!;
        setPadArray(cachedData.map((_, index) => `${padName} - ${index + 1}`));
        const sessionPad = getSession(SELECTED_PAD);
        const selectedPadData =
          cachedData.find((kirtan) => kirtan.id === kirtanId) ??
          (sessionPad ? cachedData.find((kirtan) => kirtan.pad_no === Number(sessionPad)) : undefined) ??
          cachedData[0];
        setSelectedpad(selectedPadData);
        const padValue = `${padName} - ${selectedPadData?.pad_no || 1}`;
        setPad(padValue);
        return;
      }

      // Priority 3: Fetch from API
      setIsLoadingPads(true);
      try {
        const apiService = new ApiService(lang || LANG.gujarati);
        // const data = await apiService.getKirtanLyricsPadListOfKirtan(kirtanId);
        const data: any[] = kirtanPadListData || []; // Using loader data as fallback to avoid API call during development
        if (data && data.length > 0) {
          padDataCache.set(cacheKey, data);
          setPadArray(data.map((_: any, index: number) => `${padName} - ${index + 1}`));
          const sessionPad = getSession(SELECTED_PAD);
          const selectedPadData =
            data.find((kirtan: any) => kirtan.id === kirtanId) ??
            (sessionPad ? data.find((kirtan: any) => kirtan.pad_no === Number(sessionPad)) : undefined) ??
            data[0];
          setSelectedpad(selectedPadData);
          const padValue = `${padName} - ${selectedPadData?.pad_no || 1}`;
          setPad(padValue);
        }
      } catch (error) {
      } finally {
        setIsLoadingPads(false);
      }
    })();
  }, [kirtanId, kirtanPadListData, padName, lang]);

  // 🎯 Smooth scroll to top when pad content changes
  useEffect(() => {
    if (scrollContainerRef.current && kirtanId) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        scrollContainerRef.current?.scrollTo({
          top: 0,
        });
      });
    }
  }, [kirtanId]);

  return (
    <div className="h-full flex flex-col">
      <div className="md:sticky md:top-0 bg-secondaryBackground z-30 rounded-lg">
        <div className=" w-full flex items-center justify-between p-5 gap-x-5">
          <div className="flex w-full items-center gap-x-2">
            <h1
              className={cn(
                "body_text_m md:text-xl text-textHeadingColor line-clamp-2 md:line-clamp-1 leading-2 capitalize ",
                medium,
                selectedPadList ? "" : "h-8 w-52 2xl:w-80 rounded-full skeleton-shimmer",
              )}
              title={selectedPadList?.title}
            >
              {selectedPadList ? selectedPadList?.title : ""}
            </h1>
            {selectedPadList?.tag && <Tag text={selectedPadList.tag} />}
          </div>
          <motion.button
            initial="initial"
            whileTap={"tap"}
            variants={touchVariants}
            onClick={onClose}
            className="border border-borderPrimary bg-menuBorderColor p-1 rounded-full"
          >
            <IoMdClose color={COLORS.iconOrange} />
          </motion.button>
        </div>

        <div className="border-b border-borderPrimary"></div>
      </div>
      {/* border for bottom */}
      {/* audio button */}
      <div className="flex justify-center items-center gap-3">
        {selectedPadList?.track_path && (
          <div className="p-2 rounded-full border-[1px] border-borderPrimary flex justify-center items-center">
            <FiMusic size={15} className="text-textHeadingColor" />
          </div>
        )}
      </div>
      <div className="space-y-5 p-5 overflow-y-auto custom-scrollbar" ref={scrollContainerRef}>
        {padArray && padArray.length > 0 && pad ? (
          <div className="w-full h-auto flex justify-start items-start px-1 max-md:py-2">
            <Chip
              chipData={padArray}
              selectedValue={pad || selectedPadList ? `${padName} - ${selectedPadList?.pad_no}` : ""}
              onClick={(value) => {
                handleClickOnPad(value.toString());
              }}
              className="max-w-full"
              justify="justify-start"
            />
          </div>
        ) : (
          // Minimal skeleton for pad chips - only show 2 instead of 5
          <div className="w-full overflow-x-auto scrollbar-hide flex justify-start items-start gap-4 py-2">
            {Array.from({ length: 2 }).map((_, i) => {
              return <div key={i} className="px-4 py-2 h-11 w-24 rounded-full skeleton-shimmer flex-shrink-0" />;
            })}
          </div>
        )}

        <div className="w-full p-5 rounded-[10px] overflow-x-auto custom-scrollbar bg-cardBg">
          {selectedPadList ? (
            <div
              className={cn("w-full body_text_m text-textColor space-y-1 whitespace-pre-line ", mediumInter)}
              dangerouslySetInnerHTML={{ __html: he.decode(selectedPadList.text) }}
            />
          ) : (
            // Efficient skeleton: show 3-4 lines instead of one large block
            <div className="space-y-3">
              <div className="h-4 w-full rounded skeleton-shimmer" />
              <div className="h-4 w-5/6 rounded skeleton-shimmer" />
              <div className="h-4 w-4/5 rounded skeleton-shimmer" />
              <div className="h-4 w-3/4 rounded skeleton-shimmer" />
            </div>
          )}
        </div>

        {selectedPadList?.tracks && selectedPadList.tracks.length > 0 && (
          <div className="space-y-2">
            <h3 className={cn("body_text_m text-textColorPrimary", normalInter)}>{t("kirtan-lyrics.tracks", { defaultValue: "Track" })}</h3>
            <div className="space-y-2">
              {selectedPadList.tracks.map((track, index) => {
                const authorName = rachayta?.find((item) => item.id === track.author_rachiyta_id)?.text;
                const gayakName = gayakData?.find((item) => item.id === track.vakta_gayak_id)?.text;
                const isVideo = track.media_type === "video";
                return (
                  <LocalizedLink
                    to={track.navigation_to}
                    target="_blank"
                    key={index}
                    className="flex items-center gap-2 p-3 rounded-lg bg-cardBg cursor-pointer"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-20 aspect-video">
                        <img
                          title={track.name}
                          src={getYoutubeVideoThumbnail(track.video_id || "", "medium")}
                          alt={track.name}
                          className="h-full w-full object-contain rounded"
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn("body_text_s text-textColor truncate", mediumInter)}>{track.name}</p>
                      {authorName && <p className={cn("body_text_xs text-textColorPrimary truncate", normalInter)}>{authorName}</p>}
                      {gayakName && <p className={cn("body_text_xs text-textColorPrimary truncate", normalInter)}>{gayakName}</p>}
                    </div>
                  </LocalizedLink>
                );
              })}
            </div>
          </div>
        )}

        {Object.keys(KirtanInfo).length > 0 ? (
          <div className="space-y-2">
            {Object.entries(KirtanInfo)
              .filter(([, value]) => value !== null && (Array.isArray(value) ? value.length > 0 : true))
              .map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <div className={cn("grid w-full items-start gap-2 body_text_m text-textColorPrimary", "grid-cols-[90px_auto_1fr]", normalInter)}>
                    <span className="capitalize truncate">{key.replace(/_/g, " ")}</span>
                    <span className="text-center">:</span>
                    <div className="flex flex-col gap-1">
                      {Array.isArray(value) ? (
                        value.map((item, index) => (
                          <span key={index} className="capitalize">
                            {item}
                            {index < value.length - 1 ? "," : ""}
                          </span>
                        ))
                      ) : (
                        <span className={cn("truncate capitalize", normalInter)}>{value}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default KirtanDetailPanel;
